import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { SAP_MODULE_SCOPES, SAP_SYSTEM_ROLES, type SapConnectionView, type SapModuleScope, type SapSystemEntry, type SapSystemRole } from '@/domain/models/sap-connection.js';
import type { SapConnectionController } from '@/presentation/controllers/sap-connection-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useSapConnection } from '@/presentation/hooks/use-sap-connection.js';

type Props = {
    clientId: string | undefined;
    controller: SapConnectionController;
    canEdit: boolean;
};

type SystemFormEntry = {
    name: string;
    role: SapSystemRole | '';
    moduleScope: SapModuleScope | '';
};

type FormState = {
    mcpUrl: string;
    apiKey: string;
    systems: SystemFormEntry[];
    defaultSystem: string;
    enabled: boolean;
};

const emptySystemEntry = (): SystemFormEntry => ({ name: '', role: '', moduleScope: '' });

const emptyForm = (): FormState => ({
    mcpUrl: '',
    apiKey: '',
    systems: [emptySystemEntry()],
    defaultSystem: '',
    enabled: false
});

const toSystemFormEntry = (entry: SapSystemEntry): SystemFormEntry =>
    typeof entry === 'string' ? { name: entry, role: '', moduleScope: '' } : { name: entry.name, role: entry.role ?? '', moduleScope: entry.moduleScope ?? '' };

const toSapSystemEntry = (system: SystemFormEntry): SapSystemEntry =>
    system.role || system.moduleScope
        ? { name: system.name, ...(system.role ? { role: system.role } : {}), ...(system.moduleScope ? { moduleScope: system.moduleScope } : {}) }
        : system.name;

const formFromConnection = (connection: SapConnectionView | null): FormState => {
    if (!connection) {
        return emptyForm();
    }
    return {
        mcpUrl: connection.mcpUrl,
        apiKey: '',
        systems: connection.systems.length > 0 ? connection.systems.map(toSystemFormEntry) : [emptySystemEntry()],
        defaultSystem: connection.defaultSystem ?? '',
        enabled: connection.enabled
    };
};

export function SapConnectionSection({ clientId, controller, canEdit }: Props) {
    const { translate } = useApp();
    const { connection, isLoading, error, upsert, test } = useSapConnection(controller, clientId, Boolean(clientId));
    const [form, setForm] = useState<FormState>(emptyForm());
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [testMessage, setTestMessage] = useState<string | null>(null);
    const [testSeverity, setTestSeverity] = useState<'success' | 'error' | 'info'>('info');

    useEffect(() => {
        setForm(formFromConnection(connection));
        setFormError(null);
        setTestMessage(null);
    }, [connection]);

    if (!clientId) {
        return <Alert severity="info">{translate('clients.sap.saveClientFirst')}</Alert>;
    }

    if (isLoading) {
        return <Typography color="text.secondary">{translate('common.loading')}</Typography>;
    }

    const normalizedSystemEntries = form.systems.map((system) => ({ ...system, name: system.name.trim() })).filter((system) => system.name.length > 0);
    const normalizedSystemNames = normalizedSystemEntries.map((system) => system.name);

    const systemEntriesPayload: SapSystemEntry[] = normalizedSystemEntries.map(toSapSystemEntry);

    const buildPayload = () => ({
        mcpUrl: form.mcpUrl.trim(),
        ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {}),
        systems: systemEntriesPayload,
        defaultSystem: form.defaultSystem.trim() || null,
        enabled: form.enabled
    });

    const validate = (): string | null => {
        if (!form.mcpUrl.trim().startsWith('https://')) {
            return translate('clients.sap.errors.mcpUrlHttps');
        }
        if (normalizedSystemNames.length === 0) {
            return translate('clients.sap.errors.systemsRequired');
        }
        if (form.defaultSystem && !normalizedSystemNames.includes(form.defaultSystem)) {
            return translate('clients.sap.errors.defaultSystemInvalid');
        }
        if (!connection && !form.apiKey.trim()) {
            return translate('clients.sap.errors.apiKeyRequired');
        }
        return null;
    };

    const handleSave = async () => {
        const validationError = validate();
        if (validationError) {
            setFormError(validationError);
            return;
        }
        setSaving(true);
        setFormError(null);
        setTestMessage(null);
        const result = await upsert(buildPayload());
        setSaving(false);
        if (!result) {
            setFormError(translate('clients.sap.errors.saveFailed'));
            return;
        }
        if (result.isLeft()) {
            setFormError(result.value.message);
            return;
        }
        setForm((prev) => ({ ...prev, apiKey: '' }));
        setTestMessage(translate('clients.sap.saveSuccess'));
        setTestSeverity('success');
    };

    const handleTest = async () => {
        const validationError = validate();
        if (validationError) {
            setFormError(validationError);
            return;
        }
        setTesting(true);
        setFormError(null);
        setTestMessage(null);
        const payload = buildPayload();
        // O endpoint de teste só verifica conectividade/existência dos sistemas no MCP —
        // role/moduleScope são metadado de roteamento (usado só na análise de EF) e o
        // schema do backend para este endpoint aceita apenas nomes.
        const result = await test({
            mcpUrl: payload.mcpUrl,
            apiKey: payload.apiKey,
            systems: normalizedSystemNames,
            defaultSystem: payload.defaultSystem
        });
        setTesting(false);
        if (!('ok' in result)) {
            setTestSeverity('error');
            setTestMessage(result.error);
            return;
        }
        if (result.ok) {
            setTestSeverity('success');
            setTestMessage(translate('clients.sap.testOk', { systems: result.systems.join(', ') }));
            return;
        }
        setTestSeverity('error');
        setTestMessage(translate(`clients.sap.testErrors.${result.error}`));
    };

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
                {translate('clients.sap.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {translate('clients.sap.description')}
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {formError ? <Alert severity="error">{formError}</Alert> : null}
            {testMessage ? <Alert severity={testSeverity}>{testMessage}</Alert> : null}
            <TextField
                label={translate('clients.sap.mcpUrl')}
                value={form.mcpUrl}
                onChange={(event) => setForm((prev) => ({ ...prev, mcpUrl: event.target.value }))}
                fullWidth
                size="small"
                disabled={!canEdit}
                placeholder="https://..."
            />
            <TextField
                label={translate('clients.sap.apiKey')}
                value={form.apiKey}
                onChange={(event) => setForm((prev) => ({ ...prev, apiKey: event.target.value }))}
                fullWidth
                size="small"
                type="password"
                disabled={!canEdit}
                placeholder={connection ? translate('clients.sap.apiKeyKeep') : ''}
                helperText={connection ? translate('clients.sap.apiKeyHint', { masked: connection.maskedApiKey }) : translate('clients.sap.apiKeyRequiredHint')}
            />
            <Stack spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                    {translate('clients.sap.systems')}
                </Typography>
                {form.systems.map((system, index) => (
                    <Stack key={`system-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <TextField
                            value={system.name}
                            onChange={(event) => {
                                const value = event.target.value;
                                setForm((prev) => ({
                                    ...prev,
                                    systems: prev.systems.map((entry, i) => (i === index ? { ...entry, name: value } : entry))
                                }));
                            }}
                            sx={{ flex: 2 }}
                            size="small"
                            disabled={!canEdit}
                            placeholder={translate('clients.sap.systemPlaceholder')}
                        />
                        <TextField
                            select
                            label={translate('clients.sap.role')}
                            value={system.role}
                            onChange={(event) => {
                                const value = event.target.value as SapSystemRole | '';
                                setForm((prev) => ({
                                    ...prev,
                                    systems: prev.systems.map((entry, i) => (i === index ? { ...entry, role: value } : entry))
                                }));
                            }}
                            sx={{ flex: 1, minWidth: 160 }}
                            size="small"
                            disabled={!canEdit}
                        >
                            <MenuItem value="">{translate('clients.sap.noRole')}</MenuItem>
                            {SAP_SYSTEM_ROLES.map((role) => (
                                <MenuItem key={role} value={role}>
                                    {translate(`clients.sap.roles.${role}`)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={translate('clients.sap.moduleScope')}
                            value={system.moduleScope}
                            onChange={(event) => {
                                const value = event.target.value as SapModuleScope | '';
                                setForm((prev) => ({
                                    ...prev,
                                    systems: prev.systems.map((entry, i) => (i === index ? { ...entry, moduleScope: value } : entry))
                                }));
                            }}
                            sx={{ flex: 1, minWidth: 160 }}
                            size="small"
                            disabled={!canEdit}
                        >
                            <MenuItem value="">{translate('clients.sap.noModuleScope')}</MenuItem>
                            {SAP_MODULE_SCOPES.map((moduleScope) => (
                                <MenuItem key={moduleScope} value={moduleScope}>
                                    {translate(`clients.sap.moduleScopes.${moduleScope}`)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <IconButton
                            size="small"
                            color="error"
                            disabled={!canEdit || form.systems.length <= 1}
                            onClick={() =>
                                setForm((prev) => {
                                    const systems = prev.systems.filter((_, i) => i !== index);
                                    const defaultSystem = systems.some((s) => s.name.trim() === prev.defaultSystem) ? prev.defaultSystem : '';
                                    return { ...prev, systems: systems.length > 0 ? systems : [emptySystemEntry()], defaultSystem };
                                })
                            }
                            title={translate('clients.sap.removeSystem')}
                        >
                            <Trash2 size={16} />
                        </IconButton>
                    </Stack>
                ))}
                <Box>
                    <Button
                        size="small"
                        startIcon={<Plus size={16} />}
                        disabled={!canEdit}
                        onClick={() => setForm((prev) => ({ ...prev, systems: [...prev.systems, emptySystemEntry()] }))}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('clients.sap.addSystem')}
                    </Button>
                </Box>
            </Stack>
            <TextField
                select
                label={translate('clients.sap.defaultSystem')}
                value={form.defaultSystem}
                onChange={(event) => setForm((prev) => ({ ...prev, defaultSystem: event.target.value }))}
                fullWidth
                size="small"
                disabled={!canEdit}
            >
                <MenuItem value="">{translate('clients.sap.noDefaultSystem')}</MenuItem>
                {normalizedSystemNames.map((system) => (
                    <MenuItem key={system} value={system}>
                        {system}
                    </MenuItem>
                ))}
            </TextField>
            <FormControlLabel
                control={<Switch checked={form.enabled} onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))} disabled={!canEdit} />}
                label={translate('clients.sap.enabled')}
            />
            <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => void handleTest()} disabled={!canEdit || testing || saving} sx={{ textTransform: 'none' }}>
                    {testing ? translate('clients.sap.testing') : translate('clients.sap.test')}
                </Button>
                <Button variant="contained" onClick={() => void handleSave()} disabled={!canEdit || saving || testing} sx={{ textTransform: 'none' }}>
                    {saving ? translate('clients.sap.saving') : translate('clients.sap.save')}
                </Button>
            </Stack>
        </Stack>
    );
}
